import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as Google } from "../static/Icons/google.svg";
import { getAllTools, getToolById, connectTool } from "../api/tool";
import { createServer, addActionInServer } from "../api/server";
import { getConnections } from "../api/account";
import { showErrorToast, showSuccessToast } from "../toastUtils";
import ButtonCommon from "../CommonComponents/ButtonCommon";
import CommonModal from "../CommonComponents/CommonModal";
import { useLocation, useNavigate } from "react-router-dom";

const AllApps = () => {
  const [allTools, setAllTools] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Server creation modal states
  const [openServerModal, setOpenServerModal] = useState(false);
  const [newServerName, setNewServerName] = useState("");
  const [serverLoading, setServerLoading] = useState(false);
  const [selectedToolForServer, setSelectedToolForServer] = useState(null);

  // Action selection modal states
  const [openActionModal, setOpenActionModal] = useState(false);
  const [toolActions, setToolActions] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [createdServerId, setCreatedServerId] = useState(null);

  // Final Connect Modal states
  const [finalConnectModal, setFinalConnectModal] = useState(false);
  const [isAuthCompleted, setIsAuthCompleted] = useState(false);
  const [oauthMethod, setOauthMethod] = useState("");
  const [personalAccessToken, setPersonalAccessToken] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [isBulkAdd, setIsBulkAdd] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Popular apps list (first 8 apps for Popular section)
  const popularAppsCount = 8;

  useEffect(() => {
    fetchAllTools();
  }, []);

  useEffect(() => {
    if (location.state?.reopenFinalConnectModal) {
      const stored = sessionStorage.getItem("postOAuthModalState");
      if (stored) {
        const parsed = JSON.parse(stored);
        setSelectedToolForServer(parsed.tool);
        setSelectedActions(parsed.selectedActions || []);
        setIsBulkAdd(parsed.isBulkAdd || false);
        setCreatedServerId(parsed.createdServerId);
        setFinalConnectModal(true);
        // if (oauthStatus === "true") {
        //   setIsAuthCompleted(true);
        // }
        const latestOauthStatus = sessionStorage.getItem("toolAuth");
        if (latestOauthStatus === "true") {
          setIsAuthCompleted(true);
        }
        sessionStorage.removeItem("postOAuthModalState");
      }
    }
    //eslint-disable-next-line
  }, [location.state]);

  const fetchAllTools = async () => {
    setLoading(true);
    try {
      const [toolsRes, connectionsRes] = await Promise.all([
        getAllTools(),
        getConnections(),
      ]);

      if (toolsRes.success) {
        setAllTools(toolsRes.data || []);
      } else {
        showErrorToast(toolsRes.message || "Failed to fetch tools");
      }

      if (connectionsRes.success) {
        setConnectedAccounts(connectionsRes.data.connections || []);
      } else {
        // Non-critical, so just log it or show a subtle warning
        console.error(
          "Failed to fetch connected accounts:",
          connectionsRes.message
        );
      }
    } catch (error) {
      showErrorToast("Error fetching data");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchToolActions = async (toolId) => {
    setActionLoading(true);
    try {
      const res = await getToolById(toolId);
      if (res.success && res.data) {
        // THIS IS THE FIX: Update the tool state with full details
        setSelectedToolForServer(res.data);
        setToolActions(res.data.toolActions || []);
      } else {
        showErrorToast(res.message || "Failed to fetch tool actions");
      }
    } catch (error) {
      showErrorToast("Error fetching tool actions");
      console.error("Error fetching tool actions:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const renderToolIcon = (tool, size = 40) => {
    if (tool && tool.toolIcon) {
      try {
        return (
          <img
            src={require(`../static/Icons/${tool.toolIcon}`)}
            alt={tool.toolName}
            style={{
              width: size,
              height: size,
              objectFit: "contain",
              borderRadius: 8,
            }}
          />
        );
      } catch (e) {
        // Fallback to default icon
        try {
          return (
            <img
              src={require(`../static/Icons/default-tool.svg`)}
              alt={tool.toolName}
              style={{
                width: size,
                height: size,
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          );
        } catch (err) {
          // Final fallback
          return <Google style={{ width: size, height: size }} />;
        }
      }
    }
    // Final fallback
    return <Google style={{ width: size, height: size }} />;
  };

  const filteredTools = allTools.filter(
    (tool) =>
      tool.toolName &&
      tool.toolName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const popularApps = filteredTools.slice(0, popularAppsCount);
  const allApps = searchTerm ? filteredTools : allTools;

  const handleConnectAccount = (tool) => {
    // TODO: Implement connect account functionality
    console.log("Connect account for:", tool.toolName);
  };

  // Updated handleCreateServer function
  const handleCreateServer = (tool) => {
    // Store the selected tool for later use
    setSelectedToolForServer(tool);

    // Open the server creation modal
    setOpenServerModal(true);
  };

  // Create server and then open action selection modal
  const handleServerCreation = async () => {
    if (!newServerName.trim()) {
      showErrorToast("Server name is required");
      return;
    }

    setServerLoading(true);
    try {
      const res = await createServer({ server_name: newServerName.trim() });
      if (res.success) {
        const createdServerId = res.data.server_id;
        setCreatedServerId(createdServerId);

        // Update localStorage with the new server ID for subsequent operations
        localStorage.setItem("server_id", createdServerId);

        // Close server creation modal
        setOpenServerModal(false);
        setNewServerName("");

        // Show success message
        showSuccessToast(res.message || "Server created successfully");

        // Fetch tool actions and open action selection modal
        await fetchToolActions(selectedToolForServer.toolId);
        setOpenActionModal(true);
      } else {
        showErrorToast(res.message || "Failed to create server");
      }
    } catch (err) {
      showErrorToast("Error creating server");
      console.error("Error creating server:", err);
    } finally {
      setServerLoading(false);
    }
  };

  const handleActionToggle = (action) => {
    setSelectedActions((prev) => {
      // Debug: Log to see what's being passed
      console.log("Action:", action);
      console.log("Current selected:", prev);

      // Use actionKey instead of actionId
      if (!action || !action.actionKey) {
        console.error("Invalid action or missing actionKey:", action);
        return prev;
      }

      // Use actionKey for comparison
      const isSelected = prev.some((a) => a.actionKey === action.actionKey);

      if (isSelected) {
        // Remove the action
        return prev.filter((a) => a.actionKey !== action.actionKey);
      } else {
        // Add the action (create new object to avoid reference issues)
        return [...prev, { ...action }];
      }
    });
  };
  const handleSelectAllActions = () => {
    if (selectedActions.length === toolActions.length) {
      setSelectedActions([]);
    } else {
      setSelectedActions([...toolActions]);
    }
  };

  const handleContinueWithActions = () => {
    if (selectedActions.length === 0) {
      showErrorToast("Please select at least one action");
      return;
    }
    setOpenActionModal(false);

    setFinalConnectModal(true);
    const allSelected =
      toolActions.length > 0 && selectedActions.length === toolActions.length;
    setIsBulkAdd(allSelected);
  };

  const handleToolConnect = async (method, provider) => {
    setServerLoading(true);
    try {
      const payload = {
        provider: provider?.toolName,
        scopes: Array.isArray(provider?.authScope)
          ? provider.authScope.join(",")
          : "",
        redirect: `${window.location.origin}/dashboard/oauth/callback`,
        auth_method: method,
      };

      if (method === "personal_access_token") {
        if (!personalAccessToken.trim()) {
          setTokenError("Access token is required");
          setServerLoading(false);
          return;
        }
        setTokenError("");
        payload.access_token = personalAccessToken;
      }

      const res = await connectTool(payload);

      if (method === "personal_access_token") {
        if (res?.success) {
          localStorage.setItem("oauthResponse", JSON.stringify(res));
          setIsAuthCompleted(true);
          sessionStorage.setItem("toolAuth", String(true));
          showSuccessToast("Personal access token authenticated successfully!");
        } else {
          showErrorToast(res?.message || "Authentication failed");
        }
      } else if (res?.data?.auth_url) {
        const oauthState = {
          tool: selectedToolForServer,
          selectedActions,
          isBulkAdd,
          createdServerId,
          returnTo: `/dashboard/apps`,
        };
        sessionStorage.setItem(
          "postOAuthModalState",
          JSON.stringify(oauthState)
        );
        localStorage.setItem("oauthResponse", JSON.stringify(res)); // ✅ Added
        window.location.href = res.data.auth_url;
      } else {
        showErrorToast(res?.message || "Authentication failed");
      }
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || err?.message || "Authentication failed"
      );
    } finally {
      setServerLoading(false);
    }
  };

  const handleChange = (event) => {
    const selectedMethod = event.target.value;
    setOauthMethod(selectedMethod);

    // If not personal token, call API immediately
    if (selectedMethod !== "personal_access_token") {
      handleToolConnect(selectedMethod, selectedToolForServer);
    }
  };

  const handleTokenSubmit = () => {
    if (!personalAccessToken.trim()) {
      setTokenError("Access token is required");
      return;
    }
    setTokenError("");
    handleToolConnect("personal_access_token", selectedToolForServer);
  };

  const formatLabel = (text) => {
    if (!text) return "";
    return text
      .split("_")
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(" ");
  };

  const capitalizeFirst = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const AppCard = ({ tool, showAccountStatus = false }) => {
    const isAccountConnected = connectedAccounts.some(
      (acc) =>
        acc.provider.toLowerCase() === tool.toolName.toLowerCase() &&
        acc.is_connected
    );

    return (
      <Card
        sx={{
          bgcolor: "#2C2F33",
          border: "1px solid #50505030",
          borderRadius: "12px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "#B9E307",
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(185, 227, 7, 0.2)",
          },
        }}
      >
        <CardContent
          sx={{
            p: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 220,
          }}
        >
          {/* Header with icon and name */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
          >
            <Box sx={{ flexShrink: 0 }}>{renderToolIcon(tool, 40)}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#EDEDED",
                  fontWeight: 600,
                  fontSize: "1rem",
                  mb: 0,
                }}
              >
                {capitalizeFirst(tool.toolName)}
              </Typography>
            </Box>
          </Box>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              color: "#AEAEAE",
              mb: 2,
              flex: 1,
              display: "-webkit-box",
              "-webkit-line-clamp": 2,
              "-webkit-box-orient": "vertical",
              overflow: "hidden",
              minHeight: "32px",
              lineHeight: 1.3,
              fontSize: "0.85rem",
            }}
          >
            {tool.toolDescription}
          </Typography>

          {/* Action buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              mt: "auto",
              flexDirection: "column",
            }}
          >
            <ButtonCommon
              variant="outlined"
              fullWidth
              disabled={isAccountConnected}
              sx={{
                borderRadius: "6px",
                fontSize: "0.85rem",
                textTransform: "capitalize",
                height: "36px",
                ...(isAccountConnected && {
                  borderColor: "#4CAF50 !important",
                  color: "#4CAF50 !important",
                  backgroundColor: "rgba(76, 175, 80, 0.1) !important",
                  cursor: "not-allowed !important",
                  "&:hover": {
                    borderColor: "#4CAF50 !important",
                    backgroundColor: "rgba(76, 175, 80, 0.1) !important",
                  },
                }),
              }}
              onClick={() => handleConnectAccount(tool)}
            >
              {isAccountConnected ? "Account Connected" : "Connect Account"}
            </ButtonCommon>
            <ButtonCommon
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              sx={{
                borderRadius: "6px",
                fontSize: "0.85rem",
                textTransform: "capitalize",
                height: "36px",
              }}
              onClick={() => handleCreateServer(tool)}
            >
              Create Server
            </ButtonCommon>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          bgcolor: "#000",
        }}
      >
        <CircularProgress sx={{ color: "#B9E307" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#000",
        color: "#fff",
        height: "calc(100vh - 100px)",
        overflowY: "auto",
        py: 3,
        px: { xs: 2, md: 4 },
      }}
    >
      {/* Search Section */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search your favourite apps"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            maxWidth: 600,
            "& .MuiOutlinedInput-root": {
              bgcolor: "#2C2F33",
              color: "#EDEDED",
              borderRadius: "12px",
              "& fieldset": {
                borderColor: "#50505050",
              },
              "&:hover fieldset": {
                borderColor: "#B9E307",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#B9E307",
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#AEAEAE",
              opacity: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#AEAEAE" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Popular Section */}
      {!searchTerm && (
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <StarIcon sx={{ color: "#B9E307", fontSize: 20 }} />
            <Typography
              variant="h5"
              sx={{
                color: "#EDEDED",
                fontWeight: 600,
              }}
            >
              Popular
            </Typography>
            <Chip
              label={popularAppsCount}
              size="small"
              sx={{
                bgcolor: "#B9E307",
                color: "#000",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          </Box>

          <Grid container spacing={3}>
            {popularApps.map((tool) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={tool.toolId}>
                <AppCard tool={tool} showAccountStatus={true} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* All Apps Section */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            color: "#EDEDED",
            fontWeight: 600,
            mb: 3,
          }}
        >
          {searchTerm ? `Search Results (${filteredTools.length})` : "All Apps"}
        </Typography>

        {allApps.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#AEAEAE",
                mb: 2,
              }}
            >
              {searchTerm ? "No apps found" : "No apps available"}
            </Typography>
            {searchTerm && (
              <Typography
                variant="body2"
                sx={{
                  color: "#AEAEAE",
                }}
              >
                Try searching with different keywords
              </Typography>
            )}
          </Box>
        ) : (
          <Grid container spacing={3}>
            {allApps.map((tool) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={tool.toolId}>
                <AppCard tool={tool} showAccountStatus={true} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Create Server Modal */}
      <CommonModal
        open={openServerModal}
        handleClose={() => {
          setOpenServerModal(false);
          setNewServerName("");
          setSelectedToolForServer(null);
        }}
        title="New MCP Server"
        cancelProps={{ sx: { display: "none" } }}
        submitProps={{ sx: { display: "none" } }}
      >
        <Box variant="outlined" sx={{ borderRadius: "10px" }}>
          <Box>
            <form>
              <InputLabel
                required
                sx={{
                  color: "#D6D6D6",
                  mb: 1,
                  "& .MuiInputLabel-asterisk": {
                    color: "red",
                  },
                }}
              >
                Name
              </InputLabel>
              <TextField
                fullWidth
                size="small"
                value={newServerName}
                onChange={(e) => setNewServerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleServerCreation();
                  }
                }}
                required
                sx={{
                  borderRadius: 1,
                  background: "#202020",
                  input: { color: "#fff" },
                  "& .MuiInputLabel-root": { color: "#aaa" },
                  mb: 2,
                  border: "1.39px solid #FFFFFF80",
                }}
              />

              {/* Show selected tool info */}
              {selectedToolForServer && (
                <Box sx={{ mb: 2, p: 2, bgcolor: "#2C2F33", borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: "#AEAEAE", mb: 1 }}>
                    Selected tool to add:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {renderToolIcon(selectedToolForServer, 24)}
                    <Typography variant="body2" sx={{ color: "#EDEDED" }}>
                      {capitalizeFirst(selectedToolForServer.toolName)}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box
                gap={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <ButtonCommon
                  variant="contained"
                  sx={{
                    borderRadius: 6,
                    width: "max-content",
                    boxShadow: "0px 4px 10.2px 3px #CFFF045C",
                  }}
                  size="small"
                  onClick={handleServerCreation}
                  disabled={serverLoading}
                >
                  {serverLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Create MCP Server"
                  )}
                </ButtonCommon>
              </Box>
            </form>
          </Box>
        </Box>
      </CommonModal>

      {/* Action Selection Modal */}
      <CommonModal
        open={openActionModal}
        handleClose={() => {
          setOpenActionModal(false);
          setSelectedActions([]);
          setToolActions([]);
          setCreatedServerId(null);
          setSelectedToolForServer(null);
        }}
        title={`Select ${selectedToolForServer?.toolName || "Tool"} Actions`}
        submitText="Next"
        cancelText="Cancel"
        submitProps={{
          onClick: handleContinueWithActions,
          disabled: selectedActions.length === 0,
        }}
        cancelProps={{
          onClick: () => {
            setOpenActionModal(false);
            setSelectedActions([]);
            setToolActions([]);
            setCreatedServerId(null);
            setSelectedToolForServer(null);
          },
        }}
      >
        <Box sx={{ maxWidth: 600, minHeight: 400 }}>
          {/* Tool Info Header */}
          {selectedToolForServer && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                bgcolor: "#2C2F33",
                borderRadius: 1,
              }}
            >
              {renderToolIcon(selectedToolForServer, 32)}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: "#EDEDED", fontWeight: 600 }}
                >
                  {capitalizeFirst(selectedToolForServer.toolName)}
                </Typography>
                <Typography variant="body2" sx={{ color: "#AEAEAE" }}>
                  {selectedToolForServer.toolDescription}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Search Actions */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search actions..."
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#2C2F33",
                  color: "#EDEDED",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "#50505050",
                  },
                  "&:hover fieldset": {
                    borderColor: "#B9E307",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#B9E307",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#AEAEAE",
                  opacity: 1,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#AEAEAE" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Select All Actions */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    selectedActions.length === toolActions.length &&
                    toolActions.length > 0
                  }
                  onChange={handleSelectAllActions}
                  sx={{
                    color: "#B9E307",
                    "&.Mui-checked": {
                      color: "#B9E307",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: "#EDEDED", fontWeight: 600 }}>
                  Add all{" "}
                  {capitalizeFirst(selectedToolForServer?.toolName) || "tool"}{" "}
                  actions
                </Typography>
              }
            />
          </Box>

          <Divider sx={{ borderColor: "#50505050", mb: 2 }} />

          {/* Actions List */}
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {actionLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress sx={{ color: "#B9E307" }} />
              </Box>
            ) : toolActions.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: "#AEAEAE" }}>
                  No actions available for this tool
                </Typography>
              </Box>
            ) : (
              <List>
                {toolActions.map((action) => {
                  const isSelected = selectedActions.some(
                    (a) => a.actionKey === action.actionKey
                  );

                  return (
                    <ListItem
                      key={action.actionKey}
                      dense
                      disablePadding
                      sx={{
                        background: isSelected ? "#555" : "#333",
                        padding: "10px",
                        borderRadius: "8px",
                        mb: 1,
                        cursor: "pointer",
                        transition: "background 0.2s ease",
                        "&:hover": {
                          background: isSelected ? "#666" : "#444",
                        },
                      }}
                      onClick={() => handleActionToggle(action)}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {renderToolIcon(selectedToolForServer, 24)}
                      </ListItemIcon>
                      <ListItemText
                        primary={action.actionDisplayName}
                        sx={{
                          textTransform: "capitalize",
                          color: "#fff",
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      </CommonModal>

      {/* Final Connect Modal */}
      <CommonModal
        open={finalConnectModal}
        handleClose={() => {
          setFinalConnectModal(false);
          setSelectedActions([]);
          setSelectedToolForServer(null);
          setCreatedServerId(null);
          sessionStorage.removeItem("toolAuth");
          navigate(`/dashboard/server/${createdServerId}`, {
            state: { refresh: true },
          });
        }}
        title={`Connect to ${capitalizeFirst(selectedToolForServer?.toolName)}`}
        cancelText="Cancel"
        cancelProps={{
          onClick: () => {
            setFinalConnectModal(false);
          },
        }}
        submitText="Save"
        submitProps={{
          disabled: !isAuthCompleted,
          loading: serverLoading,
          children: serverLoading ? <CircularProgress size={20} /> : "Save",
          onClick: async () => {
            setServerLoading(true);
            try {
              const currentToolAuthStatus = sessionStorage.getItem("toolAuth");
              const payload = {
                server_id: createdServerId,
                actions_data: {
                  toolId: selectedToolForServer.toolId,
                  toolName: selectedToolForServer.toolName,
                  toolDescription: selectedToolForServer.toolDescription,
                  toolIcon: selectedToolForServer.toolIcon,
                  toolActions: selectedActions,
                  toolAuth: currentToolAuthStatus,
                  authOptions: selectedToolForServer.authOptions,
                },
              };
              await addActionInServer(payload, createdServerId);
              showSuccessToast("Tool added successfully!");
              setFinalConnectModal(false);
              navigate(`/dashboard/server/${createdServerId}`, {
                state: { refresh: true },
              });
            } catch (error) {
              showErrorToast("Error saving actions");
              console.error("Error saving action(s):", error);
            } finally {
              setServerLoading(false);
              // Full cleanup
              setSelectedActions([]);
              setSelectedToolForServer(null);
              setCreatedServerId(null);
              sessionStorage.removeItem("toolAuth");
            }
          },
        }}
      >
        <Box
          variant="outlined"
          sx={{ display: "flex", flexDirection: "column", gap: 4 }}
        >
          {/* 1. GitHub Tool Auth Box */}
          <Box>
            <Typography sx={{ color: "#ECECEC", mb: 1 }}>
              {capitalizeFirst(selectedToolForServer?.toolName)} Account
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Box
                key={selectedToolForServer?.toolId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0px",
                  padding: "10px",
                  cursor: "pointer",
                  background: "#2C2F3366",
                  border: "1px solid #50505030",
                  borderRadius: 2,
                }}
              >
                <ListItemIcon>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 30,
                      height: 30,
                      bgcolor: "#2C2F3366",
                      borderRadius: 1,
                      p: 0.7,
                    }}
                  >
                    {renderToolIcon(selectedToolForServer, 30)}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={capitalizeFirst(selectedToolForServer?.toolName)}
                  sx={{
                    margin: 0,
                    "& .MuiTypography-root": {
                      color: "white",
                      fontSize: "0.95rem",
                      fontWeight: 400,
                    },
                  }}
                />

                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      minWidth: 150,
                      marginLeft: "auto",
                    }}
                  >
                    <FormControl
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: "white",
                        },
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": {
                            borderColor: "primary.main",
                          },
                          "&:hover fieldset": {
                            borderColor: "primary.dark",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                        "& .MuiSvgIcon-root": {
                          color: "white",
                        },
                      }}
                    >
                      <InputLabel id="auth-method-label">
                        Auth Option
                      </InputLabel>
                      <Select
                        labelId="auth-method-label"
                        value={oauthMethod}
                        label="Auth Option"
                        onChange={handleChange}
                      >
                        {(selectedToolForServer?.authOptions || []).map(
                          (item) => (
                            <MenuItem key={item} value={item}>
                              {formatLabel(item)}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>

              {oauthMethod === "personal_access_token" && (
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Enter Personal Access Token"
                    variant="outlined"
                    value={personalAccessToken}
                    onChange={(e) => {
                      setPersonalAccessToken(e.target.value);
                      if (tokenError) setTokenError("");
                    }}
                    error={!!tokenError}
                    helperText={tokenError}
                    sx={{
                      input: { color: "white" },
                      "& label": { color: "white" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "primary.main" },
                        "&:hover fieldset": {
                          borderColor: "primary.dark",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                  <ButtonCommon
                    variant="contained"
                    sx={{
                      width: "fit-content",
                      height: "fit-content",
                      p: 0,
                      px: 1,
                    }}
                    onClick={handleTokenSubmit}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : "Connect"}
                  </ButtonCommon>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CommonModal>
    </Box>
  );
};

export default AllApps;
